import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { FileContext } from '../context/FileContext';
import APP_CONFIG from '../config';
import { initiateStripePayment } from '../services/stripe';
import { useMutation } from 'react-query';

export function useSignFlow() {
  const {
    signFlowData,
    setSignFlowData,
    updateFormFields,
    documentRef,
    addedFormFields = [],
    isAdhocWithSignatureFieldAutoAssign,
  } = useContext(FileContext);
  const [isDeclined, setIsDeclined] = useState(false);
  const [currentPaymentItem, setCurrentPaymentItem] = useState(null);
  const signPagePaymentItem = useRef(null);

  const showDeclineSignaturePopup = signFlowData?.showDeclineSignaturePopup;
  const showPageSwitchModal = signFlowData?.showPageSwitchModal;
  const showDeclinedPDFModal = signFlowData?.showDeclinedPDFModal;
  const showPayNowDialog = signFlowData?.showPayNowDialog;
  const signingFlowAttachedFiles = signFlowData?.attachedFiles;
  const activeFile = signFlowData?.activeFile?.file;
  const activeFileId = signFlowData?.activeFile?.fileId;
  const showCompleteDialog = !!signFlowData?.showCompleteDialog;
  const showErrorSignaturePopup = signFlowData?.showErrorSignaturePopup;

  const getNextFileId = () => {
    const currentFileIndex = signingFlowAttachedFiles?.findIndex(
      (x) => x.fileId === activeFileId,
    );
    return signingFlowAttachedFiles?.[currentFileIndex + 1]?.fileId;
  };
  const nextFileId = getNextFileId();
  const pdfsRemainingToSign = signFlowData?.pdfsRemainingToSign;

  const updateSignFlowData = useCallback(
    (key, val) => {
      setSignFlowData((prev) => {
        return {
          ...prev,
          [key]: val,
        };
      });
    },
    [setSignFlowData],
  );

  const closeDeclineSignaturePopup = () => {
    if (isDeclined) {
      window.close();
    } else {
      updateSignFlowData('showDeclineSignaturePopup', false);
    }
  };

  const openDeclineSignaturePopup = () => {
    updateSignFlowData('showDeclineSignaturePopup', true);
  };

  const togglePageSwitchModal = (val) => {
    updateSignFlowData('showPageSwitchModal', val);
  };

  const setShowCompleteDialog = (val) => {
    updateSignFlowData('showCompleteDialog', val);
  };

  const setShowPayNowDialog = (val) => {
    updateSignFlowData('showPayNowDialog', val);
  };

  const setShowDeclinedPDFModal = (val) => {
    updateSignFlowData('showDeclinedPDFModal', val);
  };

  const setShowErrorSignatureModal = (val) => {
    updateSignFlowData('showErrorSignaturePopup', val)
  }

  const setParamsData = useCallback(
    (itemId, fileHistoryId) => {
      updateSignFlowData('itemId', itemId);
      updateSignFlowData('fileHistoryId', fileHistoryId);
    },
    [updateSignFlowData],
  );

  const setSigningFlowAttachedFiles = (
    val,
    isAdhocWithSignatureFieldAutoAssign = false,
  ) => {
    if (!Array.isArray(val)) return;

    if (val.length === 0) {
      updateSignFlowData('attachedFiles', []);
      updateSignFlowData('activeFile', null);
      updateSignFlowData('pdfsRemainingToSign', []);
      return;
    }
    updateSignFlowData('attachedFiles', val);
    updateSignFlowData(
      'pdfsRemainingToSign',
      val.filter((x, idx) => idx > 0).map((x) => x.fileId),
    );
    const file = val?.find((obj) => obj.fileId === val[0].fileId);
    updateSignFlowData('activeFile', file);
    !isAdhocWithSignatureFieldAutoAssign &&
      file?.assignedFields &&
      updateFormFields(file?.assignedFields);
  };

  const setActiveFile = (id) => {
    const file = signingFlowAttachedFiles?.find((obj) => obj.fileId === id);
    updateSignFlowData('activeFile', file);
    // set current addedFormFields data to handle Post data
    !isAdhocWithSignatureFieldAutoAssign
      ? file?.assignedFields && updateFormFields(file?.assignedFields)
      : updateFormFields(
          signFlowData?.signedFiles.find((x) => x.fileId === id)?.signatures ||
            [],
        );
    updatePdfsRemainingToSign(id);
  };

  const updateAssignedFields = (fileId, assignedFields) => {
    const updateFields = (fields, newFields) =>
      fields?.map((field) => {
        if (field.itemId === 'sd-payment') {
          const newField = newFields.find(
            (newField) => newField.id === field.id,
          );
          return newField ? { ...field, ...newField } : field;
        }
        return field;
      }) || [];

    setSignFlowData((prevState) => {
      const updatedAttachedFiles =
        prevState?.attachedFiles?.map((item) => {
          if (item.fileId === fileId) {
            return {
              ...item,
              assignedFields: updateFields(
                item.assignedFields ?? [],
                assignedFields,
              ),
            };
          }
          return item;
        }) || [];

      const updatedActiveFile =
        prevState?.activeFile?.fileId === fileId
          ? {
              ...prevState?.activeFile,
              assignedFields: updateFields(
                prevState?.activeFile?.assignedFields,
                assignedFields,
              ),
            }
          : prevState?.activeFile || null;

      return {
        ...prevState,
        attachedFiles: updatedAttachedFiles,
        activeFile: updatedActiveFile,
      };
    });
    updateFormFields(updateFields(addedFormFields, assignedFields));
  };

  const handleFormFieldData = (fileId) => {
    if (!fileId) return;
    const signedFiles = signFlowData?.signedFiles || [];
    const newFile = {
      fileId: fileId,
      signatures: addedFormFields.filter((item) => {
        return [
          'Sender Signature',
          'Sender Initials',
          'Receiver Signature',
          'Receiver Initials',
          'Added Signature',
          APP_CONFIG.form_fields.initialsField.title,
          APP_CONFIG.form_fields.signatureField.title,
        ].includes(item.title);
      }),
      standardFields:
        addedFormFields.filter((item) =>
          [
            'sign-date',
            'checkbox',
            'text-box',
            'sd-dropdown',
            'sd-image',
            APP_CONFIG.form_fields.standard_fields.datePicker.id,
          ].includes(item.itemId),
        ) || [],
    };
    const index = signedFiles?.findIndex((file) => file?.fileId === fileId);
    if (index !== -1) {
      signedFiles[index] = newFile;
    } else {
      signedFiles.push(newFile);
    }
    updateSignFlowData('signedFiles', signedFiles);
  };

  useEffect(() => {
    if (addedFormFields?.length || isAdhocWithSignatureFieldAutoAssign) {
      handleFormFieldData(activeFileId);
    }
  }, [addedFormFields, activeFileId]);

  const scrollToTop = () => {
    documentRef.current?.scroll({
      top: 0,
      behavior: 'smooth',
    });
  };

  const updatePdfsRemainingToSign = (id) => {
    const updatedData = (pdfsRemainingToSign || []).filter((x) => x !== id);
    updateSignFlowData('pdfsRemainingToSign', updatedData);
  };

  const { mutate: initatePayment, isLoading: isPaymentLoading } = useMutation(
    initiateStripePayment,
    {
      onMutate: () => {
        // Open a blank tab before the mutation to avoid iOS blocking it
        const newTab = window.open('', '_blank');
        return { newTab };
      },
      onSuccess: (res, variables, context) => {
        setCurrentPaymentItem(null);
        if (context.newTab) {
          context.newTab.location.href = res?.data;
        }
      },
    },
  );

  const initiatePaymentSession = async ({
    item,
    itemId,
    fileHistoryId,
    activeFileId,
  }) => {
    setCurrentPaymentItem(item?.id);
    initatePayment({
      item,
      itemId,
      fileHistoryId,
      activeFileId,
    });
  };

  return {
    showDeclineSignaturePopup,
    openDeclineSignaturePopup,
    closeDeclineSignaturePopup,
    isDeclined,
    setIsDeclined,
    showDeclinedPDFModal,
    setShowDeclinedPDFModal,
    showErrorSignaturePopup,
    setShowErrorSignatureModal,
    signingFlowAttachedFiles,
    setSigningFlowAttachedFiles,
    signFlowData,
    activeFile,
    activeFileId,
    setActiveFile,
    nextFileId,
    scrollToTop,
    pdfsRemainingToSign,
    showPageSwitchModal,
    togglePageSwitchModal,
    setParamsData,
    showCompleteDialog,
    setShowCompleteDialog,
    initiatePaymentSession,
    isPaymentLoading,
    currentPaymentItem,
    updateAssignedFields,
    setShowPayNowDialog,
    showPayNowDialog,
    signPagePaymentItem,
  };
}
